// frontend/src/pages/dispatch/DispatchOrderDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, Divider, List, ListItem, ListItemText, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, IconButton
} from '@mui/material';
import {
  ArrowBack, LocalShipping, Factory,
  CheckCircle, Inventory, CalendarToday,
  Build, Schedule, Undo
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import DeliveryAddressCard from '../../components/DeliveryAddressCard';
import usePageTitle from '../../hooks/usePageTitle';
import OrderV2ProductionSection from '../manufacturing/v2/components/OrderV2ProductionSection';

export default function DispatchOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [unshipLine, setUnshipLine] = useState(null);
  const [unshipQty, setUnshipQty] = useState(0);
  const [unshipping, setUnshipping] = useState(false);
  
  usePageTitle(order ? `Order: ${order.order_number}` : null);

  useEffect(() => {
    loadOrderDetails();
    loadWorkOrders();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.get(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrders = async () => {
    try {
      const response = await manufacturingAPI.workOrders.list();
      const allWorkOrders = response.data.results || response.data;
      const filtered = allWorkOrders.filter(wo =>
        wo.customer_order === parseInt(orderId)
      );
      setWorkOrders(filtered);
    } catch (error) {
      console.error('Failed to load work orders:', error);
    }
  };

  const openUnshipDialog = (line) => {
    setUnshipLine(line);
    setUnshipQty(line.quantity_shipped || 0);
  };

  const handleUnship = async () => {
    if (!unshipLine || unshipQty <= 0) return;

    setUnshipping(true);
    try {
      const response = await manufacturingAPI.customerOrders.unshipQuantity(orderId, {
        line_id: unshipLine.id,
        quantity: unshipQty
      });
      toast.success(response.data.message || 'Shipment reversed');
      (response.data.consignments_deleted || []).forEach(number =>
        toast.info(`Consignment ${number} cancelled at DX and deleted - create a new one to re-ship`)
      );
      (response.data.consignment_warnings || []).forEach(warning => toast.warn(warning));
      setUnshipLine(null);
      await loadOrderDetails();
    } catch (error) {
      console.error('Failed to unship:', error);
      toast.error(error.response?.data?.error || 'Failed to unship');
    } finally {
      setUnshipping(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'info',
      'confirmed': 'primary',
      'in_production': 'warning',
      'ready_to_ship': 'success',
      'partially_shipped': 'primary',
      'complete': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'New',
      'confirmed': 'Confirmed',
      'in_production': 'In Production',
      'ready_to_ship': 'Ready to Ship',
      'partially_shipped': 'Partially Shipped',
      'complete': 'Complete',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getDaysUntilDue = (dueDate) => {
    const days = Math.floor((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const hasItemsToShip = () => {
    // quantity_allocated already represents only the current remaining allocation
    // (shipping decrements allocations), so allocated IS the shippable amount
    return order?.lines?.some(line => {
      return (line.quantity_allocated || 0) > 0;
    });
  };

  const getTotalWeight = () => {
    if (!order?.lines) return 0;
    return order.lines.reduce((total, line) => {
      const qty = line.quantity_allocated || 0;
      const weight = line.product_weight || 0;
      return total + (qty * weight);
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading Order Details...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Order not found</Alert>
        <Button onClick={() => navigate('/dispatch')} sx={{ mt: 2 }}>
          Back to Dispatch
        </Button>
      </Box>
    );
  }

  const daysUntilDue = getDaysUntilDue(order.due_date);
  const isOverdue = daysUntilDue < 0;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping color="primary" />
              {order.order_number}
              <Chip 
                label={getStatusLabel(order.status)} 
                color={getStatusColor(order.status)}
                sx={{ ml: 2 }}
              />
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Customer: <strong>{order.customer_name}</strong>
            </Typography>
            {order.customer_reference && (
              <Typography variant="body2" color="text.secondary">
                Reference: {order.customer_reference}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasItemsToShip() && (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={<LocalShipping />}
                onClick={() => navigate(`/dispatch/orders/${orderId}/ship`)}
              >
                Ship This Order
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Alert Banners */}
      {isOverdue && order.status !== 'complete' && order.status !== 'cancelled' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>This order is {Math.abs(daysUntilDue)} days overdue!</strong>
        </Alert>
      )}

      {hasItemsToShip() && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          <strong>Items ready to ship!</strong> Click "Ship This Order" to proceed.
        </Alert>
      )}

      {!hasItemsToShip() && order.status !== 'complete' && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>
          <strong>Waiting for production.</strong> No items available to ship yet.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Order Details */}
        <Grid item xs={12} md={8}>
          {/* Order Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Dispatch Summary</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Order Date</Typography>
                      <Typography variant="body2">{formatDate(order.order_date)}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule fontSize="small" color={isOverdue ? 'error' : 'action'} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Due Date</Typography>
                      <Typography 
                        variant="body2" 
                        color={isOverdue ? 'error.main' : 'text.primary'}
                        fontWeight={isOverdue ? 'bold' : 'normal'}
                      >
                        {formatDate(order.due_date)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Items</Typography>
                    <Typography variant="h6">{order.total_quantity_ordered || 0}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Est. Weight</Typography>
                    <Typography variant="h6">{getTotalWeight().toFixed(2)} kg</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Progress */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Shipping Progress
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {order.total_quantity_shipped || 0} / {order.total_quantity_ordered || 0} shipped
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={order.completion_percentage || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                  color={order.completion_percentage >= 100 ? 'success' : 'primary'}
                />
              </Box>

              {/* Quantity Summary */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h4">{order.total_quantity_ordered || 0}</Typography>
                    <Typography variant="caption">Ordered</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h4">
                      {order.lines?.reduce((sum, line) => sum + (line.quantity_allocated || 0), 0) || 0}
                    </Typography>
                    <Typography variant="caption">Ready</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4">{order.total_quantity_shipped || 0}</Typography>
                    <Typography variant="caption">Shipped</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Order Line Items - NO PRICES */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Items</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Line</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Ready</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">To Ship</TableCell>
                      <TableCell align="right">Weight</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.lines && order.lines.map((line) => {
                      const allocated = line.quantity_allocated || 0;
                      const shipped = line.quantity_shipped || 0;
                      const ordered = line.quantity_ordered || 0;
                      const toShip = allocated;
                      const lineWeight = toShip * (line.product_weight || 0);

                      return (
                        <TableRow 
                          key={line.id}
                          sx={{
                            bgcolor: toShip > 0 ? 'success.50' : 'inherit'
                          }}
                        >
                          <TableCell>{line.line_number}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {line.product_display}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {line.product_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {ordered}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={allocated}
                              size="small"
                              color={allocated >= (ordered - shipped) ? 'success' : allocated > 0 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">{shipped}</TableCell>
                          <TableCell align="right">
                            {toShip > 0 ? (
                              <Chip 
                                label={toShip}
                                size="small"
                                color="success"
                                icon={<LocalShipping />}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {lineWeight > 0 ? `${lineWeight.toFixed(2)} kg` : '-'}
                          </TableCell>
                          <TableCell>
                            {shipped >= ordered ? (
                              <Chip label="Shipped" size="small" color="success" icon={<CheckCircle />} />
                            ) : toShip > 0 ? (
                              <Chip label="Ready" size="small" color="warning" icon={<LocalShipping />} />
                            ) : (
                              <Chip label="Waiting" size="small" variant="outlined" icon={<Schedule />} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {shipped > 0 && (
                              <Tooltip title="Unship - reverse a shipment on this line">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => openUnshipDialog(line)}
                                >
                                  <Undo fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* V2 Production (compact; additive — never breaks V1 dispatch view) */}
              <OrderV2ProductionSection orderId={orderId} variant="compact" />
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Delivery & Production Info */}
        <Grid item xs={12} md={4}>
          {/* Delivery Information - Using Reusable Component */}
          <DeliveryAddressCard 
            order={order} 
            onUpdate={loadOrderDetails}
            showContactInfo={true}
          />

          {/* Production Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Factory sx={{ mr: 1, verticalAlign: 'middle' }} />
                Production Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" color="primary">
                      {order.production_status?.total_work_orders || 0}
                    </Typography>
                    <Typography variant="caption">Total WOs</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h5">
                      {order.production_status?.completed || 0}
                    </Typography>
                    <Typography variant="caption">Completed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Typography variant="h5">
                      {order.production_status?.in_progress || 0}
                    </Typography>
                    <Typography variant="caption">In Progress</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.200' }}>
                    <Typography variant="h5">
                      {order.production_status?.pending || 0}
                    </Typography>
                    <Typography variant="caption">Pending</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Work Orders List */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Work Orders</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {workOrders.length === 0 ? (
                <Alert severity="info" icon={<Schedule />}>
                  No work orders created yet.
                </Alert>
              ) : (
                <List dense disablePadding>
                  {workOrders.map((wo) => (
                    <ListItem 
                      key={wo.id}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        px: 1.5
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {wo.work_order_number}
                            </Typography>
                            <Chip 
                              label={wo.status} 
                              size="small" 
                              color={
                                wo.status === 'completed' ? 'success' : 
                                wo.status === 'in_progress' ? 'warning' : 
                                'default'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption">
                            {wo.output_item_display} | {wo.quantity_completed}/{wo.quantity_to_produce}
                          </Typography>
                        }
                      />
                      {wo.status === 'completed' && <CheckCircle color="success" fontSize="small" />}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {order.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Unship Dialog */}
      <Dialog open={!!unshipLine} onClose={() => !unshipping && setUnshipLine(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unship Items</DialogTitle>
        <DialogContent>
          {unshipLine && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{unshipLine.product_display}</strong> — {unshipLine.quantity_shipped} shipped
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This returns the items to stock, re-allocates them to this order line,
                and rolls the order status back so it can be shipped again. ALL of this
                order's DX consignments are cancelled and deleted — new ones must be made.
              </Alert>
              <TextField
                label="Quantity to unship"
                type="number"
                size="small"
                fullWidth
                value={unshipQty}
                onChange={(e) => {
                  const max = unshipLine.quantity_shipped || 0;
                  setUnshipQty(Math.min(Math.max(0, parseInt(e.target.value) || 0), max));
                }}
                inputProps={{ min: 1, max: unshipLine.quantity_shipped || 0 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshipLine(null)} disabled={unshipping}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Undo />}
            onClick={handleUnship}
            disabled={unshipping || unshipQty <= 0}
          >
            {unshipping ? 'Unshipping...' : 'Unship'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}